import {
  Button,
  Chip,
  CircularProgress,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Box,
  IconButton,
  FormControl,
  Alert,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import React, { useEffect, useState } from "react";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Type from "./Type";
import Autocomplete from "@mui/material/Autocomplete"; // Added import
import useFetchObject from "../../../../api/useFetchObject";

const EditProcedure = ({ onClose, id }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  const auth = useAuth();
  const token = auth?.user?.token;
  const [type, setType] = useState("");

  const { data, isLoading, isError } = useFetchObject(
    ["procedure"],
    "procedure",
    id,
    token
  );

  // New effect to update formData when fetched data is available
  const [formData, setFormData] = useState({
    type: "",
    procedure_date: "",
    procedure_day: "",
    status: "",
    period: "",
    doctor: "",
    lab: [],
    total_cost: "",
    advance: "",
    exchange_rate: "",
    currency: "",
    advance_currency: "",
    zone: "",
    description: "",
    patient: "",
    next_visit_days: "",
    next_visit_date: "",
  });
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imageErrors, setImageErrors] = useState([]);

  useEffect(() => {
    if (data) {
      const formattedProcedureDate = data.procedure_date
        ? new Date(data.procedure_date).toISOString().slice(0, 16)
        : "";
      setFormData({
        type: data.type || "",
        procedure_date: formattedProcedureDate,
        procedure_day: data.procedure_day || "",
        status: data.status || "",
        period: data.period || "",
        doctor: data.doctor || "",
        lab: data.lab || [],
        total_cost: data.total_cost || "",
        discount: data.discount || "",
        advance: data.advance || "",
        advance_currency: data.advance_currency || "",
        exchange_rate: data.exchange_rate || "",
        currency: data.currency || "",
        zone: data.zone || "",
        description: data.description || "",
        patient: data.patient || "",
        next_visit_days: data.next_visit_days || "",
        next_visit_date: data.next_visit_date || "",
      });
      setExistingImages(data.images || []);
    }
  }, [data]);

  // Auto-calculate next_visit_date when procedure_date or next_visit_days change.
  useEffect(() => {
    if (formData.next_visit_days) {
      const daysToAdd = parseInt(formData.next_visit_days, 10);
      if (!isNaN(daysToAdd)) {
        const baseDate = formData.procedure_date
          ? new Date(formData.procedure_date)
          : new Date();
        baseDate.setDate(baseDate.getDate() + daysToAdd);
        const year = baseDate.getFullYear();
        const month = ("0" + (baseDate.getMonth() + 1)).slice(-2);
        const day = ("0" + baseDate.getDate()).slice(-2);
        const formattedDate = `${year}-${month}-${day}`;
        setFormData((prev) => ({
          ...prev,
          next_visit_date: formattedDate,
        }));
      }
    }
  }, [formData.procedure_date, formData.next_visit_days]);

  // Fetch users
  const {
    data: users,
    isLoading: isLoadingUser,
    isError: isErrorUser,
  } = useFetchObjects(["users"], "users/?position=7", token);

  // Fetch types
  const {
    data: typesData,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], `procedure-type/?type=2`, token);

  // Fetch patients
  const {
    data: patients,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
  } = useFetchObjects(["patients"], "patients?page_size=500", token);

  // Fetch periods
  const {
    data: periods,
    isLoading: isLoadingPeriod,
    isError: isErrorPeriod,
  } = useFetchObjects(["period"], "period", token);

  // Fetch labs
  const {
    data: labs,
    isLoading: isLoadingLab,
    isError: isErrorLab,
  } = useFetchObjects(["lab"], "lab", token);

  const {
    handleUpdate,
    loading: loadingEdit,
    success,
  } = useUpdate("procedure", token);

  const { handleDelete, ConfirmDialog } = useDelete("procedure-type", token);
  const {
    handleDelete: handleDeletePeriod,
    ConfirmDialog: PeriodConfirmDialog,
  } = useDelete("period", token);
  const { handleDelete: handleDeleteLab, ConfirmDialog: LabConfirmDialog } =
    useDelete("lab", token);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewImageChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const updatedImages = [...newImages];
      updatedImages[index] = file;
      setNewImages(updatedImages);
    }
  };

  const addNewImageField = () => {
    setNewImages([...newImages, null]);
  };

  const removeNewImageField = (index) => {
    const updatedImages = [...newImages];
    updatedImages.splice(index, 1);
    setNewImages(updatedImages);
  };

  const handleRemoveExistingImage = (imageId) => {
    setRemovedImageIds((prev) => [...prev, imageId]);
    setExistingImages((prevImages) =>
      prevImages.filter((image) => image.id !== imageId)
    );
  };

  const validateImages = () => {
    let isValid = true;
    const errors = [];
    newImages.forEach((image, index) => {
      if (!image) {
        errors[index] = t("call_center.imageRequired");
        isValid = false;
      } else if (image.size > 2 * 1024 * 1024) {
        errors[index] = t("call_center.imageTooLarge");
        isValid = false;
      } else if (!image.type.startsWith("image/")) {
        errors[index] = t("call_center.invalidImageType");
        isValid = false;
      }
    });
    setImageErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateImages()) {
      return;
    }
    const submitData = new FormData();
    submitData.append("type", formData.type);
    if (formData.procedure_date) {
      submitData.append("procedure_date", formData.procedure_date);
    }
    submitData.append("procedure_day", formData.procedure_day);
    submitData.append("status", formData.status);
    submitData.append("period", formData.period);
    submitData.append("doctor", formData.doctor);
    submitData.append("total_cost", formData.total_cost);
    submitData.append("discount", formData.discount);
    submitData.append("advance", formData.advance);
    submitData.append("exchange_rate", formData.exchange_rate);
    submitData.append("currency", formData.currency);
    submitData.append("zone", formData.zone);
    submitData.append("description", formData.description);
    submitData.append("next_visit_date", formData.next_visit_date);
    submitData.append("patient", formData.patient);

    if (Array.isArray(formData.lab)) {
      formData.lab.forEach((labId) => {
        submitData.append("lab", labId);
      });
    }

    newImages.forEach((image) => {
      if (image) submitData.append("images", image);
    });
    removedImageIds.forEach((imageId) => {
      submitData.append("remove_images", imageId);
    });

    handleUpdate(data?.id, submitData);
  };

  const handleOpenDialog = () => {
    setOpen(!open);
  };

  const handleDeleteChip = (valueToRemove) => {
    setFormData((prev) => ({
      ...prev,
      lab: prev.lab.filter((value) => value !== valueToRemove),
    }));
  };

  if (isError) {
    return (
      <Grid container justifyContent={"center"} mt={4}>
        <Alert severity="error">Field to load page</Alert>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container justifyContent={"center"} mt={4} padding={"20px"}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center">
      <Grid item xs={11.5} mt={2}>
        <Paper
          sx={{ padding: "20px", borderRadius: "10px", boxShadow: 3 }}
          component="form"
          onSubmit={handleSubmit}
        >
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="bold"
            align="center"
            sx={{ borderBottom: "3px solid #007471", pb: 1.5 }}
          >
            {data
              ? t("call_center.editProcedure")
              : t("call_center.addNewProcedure")}
          </Typography>
          <Grid container spacing={3} mt={1}>
            {/* PATIENT */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.patient")}</FormLabel>
              {isLoadingPatient ? (
                <Grid container justifyContent="center" padding="10px">
                  <CircularProgress />
                </Grid>
              ) : isErrorPatient ? (
                <Typography color="error">
                  {t("call_center.errorField")}
                </Typography>
              ) : (
                <Autocomplete
                  options={patients?.results || []}
                  value={
                    patients?.results?.find(
                      (p) => p.id === formData?.patient
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      patient: newValue ? newValue.id : "",
                    }));
                  }}
                  // Perform an exact phone match if the input is numeric,
                  // else partial match on the patient's name.
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.trim().toLowerCase();
                    return options.filter((option) => {
                      const fullName = `${option.firstname} ${
                        option.lastname || ""
                      }`.toLowerCase();
                      const phone = (option.phone || "").toLowerCase();
                      // If user input is purely numeric, exact match on phone:
                      if (/^\d+$/.test(inputValue)) {
                        return phone === inputValue;
                      }
                      // Otherwise, partial match on full name:
                      return fullName.includes(inputValue);
                    });
                  }}
                  getOptionLabel={(option) =>
                    `${option.firstname} ${option.lastname} - ${option.phone}`
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={t("call_center.patient")}
                    />
                  )}
                />
              )}
            </Grid>

            {/* TYPE */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.type")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="type"
                value={formData?.type}
                onChange={handleInputChange}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                  onClick={() => {
                    setType("type");
                    handleOpenDialog();
                  }}
                >
                  {t("call_center.addNewType")}
                </Button>
                {isLoadingType && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorType && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {typesData?.map((item, i) => (
                  <MenuItem value={item?.id} key={i}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography>{item?.name}</Typography>
                      {formData.type !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormLabel>{t("call_center.procedureDay")}</FormLabel>
                <Select
                  labelId="procedure_date-label"
                  id="procedure_date"
                  name="procedure_day"
                  value={formData?.procedure_day || ""}
                  onChange={handleInputChange}
                  label={t("call_center.procedureDay")}
                >
                  <MenuItem value="monday">{t("call_center.monday")}</MenuItem>
                  <MenuItem value="tuesday">
                    {t("call_center.tuesday")}
                  </MenuItem>
                  <MenuItem value="wednesday">
                    {t("call_center.wednesday")}
                  </MenuItem>
                  <MenuItem value="thursday">
                    {t("call_center.thursday")}
                  </MenuItem>
                  <MenuItem value="friday">{t("call_center.friday")}</MenuItem>
                  <MenuItem value="saturday">
                    {t("call_center.saturday")}
                  </MenuItem>
                  <MenuItem value="sunday">{t("call_center.sunday")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* PROCEDURE DATE */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.procedureDate")}</FormLabel>
              <TextField
                fullWidth
                type="datetime-local"
                variant="outlined"
                name="procedure_date"
                value={formData?.procedure_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* STATUS with Updated Options */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.status")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="status"
                value={formData?.status}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.done")}</MenuItem>
                <MenuItem value={2}>{t("call_center.cancel")}</MenuItem>
                <MenuItem value={3}>{t("call_center.followUP")}</MenuItem>
                <MenuItem value={4}>{t("call_center.inProgress")}</MenuItem>
                <MenuItem value={5}>{t("call_center.dontWant")}</MenuItem>
                <MenuItem value={6}>{t("call_center.nextDate")}</MenuItem>
                <MenuItem value={7}>{t("call_center.pending")}</MenuItem>
                <MenuItem value={8}>{t("call_center.fixed")}</MenuItem>
                <MenuItem value={9}>{t("call_center.flexible")}</MenuItem>
                <MenuItem value={10}>{t("call_center.noAnswer")}</MenuItem>
                <MenuItem value={11}>{t("call_center.information")}</MenuItem>
                <MenuItem value={12}>{t("call_center.comingSoon")}</MenuItem>
                <MenuItem value={13}>{t("call_center.block")}</MenuItem>
              </TextField>
            </Grid>

            {/* PERIOD */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.period")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="period"
                value={formData?.period}
                onChange={handleInputChange}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                  onClick={() => {
                    setType("period");
                    handleOpenDialog();
                  }}
                >
                  {t("call_center.addNewPeriod")}
                </Button>
                {isLoadingPeriod && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorPeriod && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {periods?.map((item, i) => (
                  <MenuItem value={item?.id} key={i}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography>{item?.name}</Typography>
                      {formData.period !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePeriod(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* DOCTOR */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.doctor")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="doctor"
                value={formData?.doctor}
                onChange={handleInputChange}
              >
                {isLoadingUser && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorUser && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {users?.map((item, i) => (
                  <MenuItem value={item?.id} key={i}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography>
                        {item?.first_name} {item?.last_name}
                      </Typography>
                    </Grid>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* LAB */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.lab")}</FormLabel>
              <Select
                multiple
                fullWidth
                variant="outlined"
                name="lab"
                value={formData.lab}
                onChange={handleInputChange}
                renderValue={(selected) => (
                  <div>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={labs?.find((item) => item.id === value)?.name}
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleDeleteChip(value);
                        }}
                      />
                    ))}
                  </div>
                )}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                  onClick={() => {
                    setType("lab");
                    handleOpenDialog();
                  }}
                >
                  {t("call_center.addNewLab")}
                </Button>
                {isLoadingLab && (
                  <Grid container justifyContent="center" padding="10px">
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorLab && (
                  <Typography color="error">
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {labs?.map((item, i) => (
                  <MenuItem value={item.id} key={i}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography>{item.name}</Typography>
                      {formData.lab?.indexOf(item.id) === -1 && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLab(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* ZONE - New Field */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.zone")}</FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="zone"
                value={formData?.zone}
                onChange={handleInputChange}
              />
            </Grid>

            {/* TOTAL PRICE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.totalPrice")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="total_cost"
                value={formData?.total_cost}
                onChange={handleInputChange}
              />
            </Grid>

            {/* CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.currency")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="currency"
                value={formData?.currency}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* ADVANCE - New Field */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.advance")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="advance"
                value={formData?.advance}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Advance CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.advanceCurrency")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="advance_currency"
                value={formData?.advance_currency}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* Discount */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.discount")} (%)</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="discount"
                value={formData?.discount}
                onChange={handleInputChange}
              />
            </Grid>

            {/* EXCHANGE RATE - New Field */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.exchangeRate", "Exchanger Rate")}
              </FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="exchange_rate"
                value={formData?.exchange_rate}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DAYS */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDays")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="next_visit_days"
                value={formData?.next_visit_days}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DATE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDate")}</FormLabel>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                name="next_visit_date"
                value={formData?.next_visit_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* DESCRIPTION - New Field */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.description", "Description")}
              </FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="description"
                value={formData?.description}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Existing Images */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  backgroundColor: "#417690",
                  color: "white",
                  padding: "10px",
                  borderRadius: "5px",
                }}
              >
                {t("call_center.existingImages")}
              </Typography>
              {existingImages.map((image) => (
                <Box
                  key={image.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "5px",
                    marginBottom: "10px",
                  }}
                >
                  <Typography variant="body2">
                    <a
                      href={image.image || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {image.image
                        ? image.image.split("/").pop()
                        : "Image not available"}
                    </a>
                  </Typography>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveExistingImage(image.id)}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                </Box>
              ))}
            </Grid>

            {/* New Images */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  backgroundColor: "#417690",
                  color: "white",
                  padding: "10px",
                  borderRadius: "5px",
                }}
              >
                {t("call_center.newImages")}
              </Typography>
              {newImages.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <input
                    type="file"
                    onChange={(e) => handleNewImageChange(e, index)}
                  />
                  <IconButton
                    color="error"
                    onClick={() => removeNewImageField(index)}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                onClick={addNewImageField}
                startIcon={<AddIcon />}
                variant="outlined"
                sx={{ marginTop: "10px" }}
              >
                {t("call_center.addImage")}
              </Button>
              {imageErrors.map((error, index) => (
                <Typography key={index} color="error" fontSize="small">
                  {error}
                </Typography>
              ))}
            </Grid>

            <Grid item xs={12} textAlign="end">
              <LoadingButton
                loading={loadingEdit}
                variant="contained"
                color="success"
                type="submit"
                sx={{ padding: "10px 20px" }}
              >
                {t("call_center.save")}
              </LoadingButton>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <ConfirmDialog />
      <LabConfirmDialog />
      <PeriodConfirmDialog />
      <Type open={open} onClose={handleOpenDialog} token={token} type={type} />
    </Grid>
  );
};

export default EditProcedure;
